import { useState } from 'react';
import { ThemeProvider, Toast } from '@gupshup-frontend/gupshup-ts-components';
import { ChannelConfig } from '@gs/gs-integration-component';
import { createOrg, addChannel, updateChannel, deleteChannel, validateEnterprise, subscribeInbound } from '../../api/gsIntg';
import { trimMultipleSpacesToSingle } from '../../helper/utils';
import { CHANNEL_FIELDS, GUPSHUP_THEME_CONFIG } from '../../utils/constants';

const isBlank = (value) => !value || !String(value).trim();
const digitsOnly = (value) => (value || '').replace(/[^0-9]/g, '');

// Bloomreach/consent credentials are set by ops on the org doc directly, not via this UI.
const AccountSettings = ({ orgId, org, reloadOrg }) => {
  const enterpriseChannels = (org?.channels || []).filter((channel) => channel.channelType === 'enterprise');

  const [toast, setToast] = useState({ open: false, title: '', description: '', type: 'success' });
  const [resetCount, setResetCount] = useState(0);

  const showToast = (title, description, type = 'success') => setToast({ open: true, title, description, type });

  const ensureOrgExists = async () => (org ? org : createOrg(orgId));

  const channelCallback = async (data) => {
    if (isBlank(data.channelName) || isBlank(data.userId) || isBlank(data.password) || isBlank(data.whatsappNo)) {
      return 1;
    }
    const whatsappNo = digitsOnly(data.whatsappNo);
    const duplicate = enterpriseChannels.find((channel) => channel.userId === data.userId && channel.whatsappNo === whatsappNo);
    if (duplicate) return 2;

    const validationResult = await validateEnterprise(data.userId, data.password);
    if (!validationResult) return 3;

    await ensureOrgExists();
    const newChannel = await addChannel(orgId, {
      channelName: trimMultipleSpacesToSingle(data.channelName),
      channelType: 'enterprise',
      receiveInboundMessage: true,
      userId: data.userId,
      password: data.password,
      whatsappNo,
    });

    try {
      await subscribeInbound(orgId, newChannel.channelId);
    } catch {
      showToast('Inbound webhook not enabled', 'Channel was added, but Bloomreach could not enable the inbound webhook for it. Contact support if this persists.', 'warning');
    }

    reloadOrg();
    return 0;
  };

  const channelUpdateCallback = async ({ formData }) => {
    if (isBlank(formData.channelName) || isBlank(formData.userId) || isBlank(formData.password) || isBlank(formData.whatsappNo)) {
      return 1;
    }
    const validationResult = await validateEnterprise(formData.userId, formData.password);
    if (!validationResult) return 3;

    await updateChannel(orgId, formData.channelId, {
      channelName: trimMultipleSpacesToSingle(formData.channelName),
      channelType: 'enterprise',
      receiveInboundMessage: true,
      userId: formData.userId,
      password: formData.password,
      whatsappNo: digitsOnly(formData.whatsappNo),
    });
    reloadOrg();
    return 0;
  };

  const handleDelete = async (selectedChannel) => {
    try {
      await deleteChannel(orgId, selectedChannel?.channelId);
      setResetCount((prev) => prev + 1);
      reloadOrg();
    } catch {
      showToast('Could not delete channel', 'Please try again.', 'error');
    }
  };

  const handleCancel = () => {};

  const channelTypes = [
    {
      label: 'Account Details',
      value: 'enterprise',
      fields: CHANNEL_FIELDS,
      callback: channelCallback,
      updateCallback: channelUpdateCallback,
    },
  ];

  const channels = enterpriseChannels.map((channel, index) => ({
    label: channel.channelName,
    value: String(index + 1),
    type: 'enterprise',
    tab: 0,
    ...channel,
  }));

  return (
    <ThemeProvider config={GUPSHUP_THEME_CONFIG}>
      <div className="br-account">
        {/* Real JSX text (not CSS ::before) since this markup is ours, not a shared component. */}
        <div className="br-section-header">
          <div className="br-section-header__title">WhatsApp channels</div>
          <div className="br-section-header__subtitle">Add and manage the Gupshup HSM accounts Bloomreach can send from.</div>
        </div>
        <ChannelConfig
          key={resetCount}
          channels={channels}
          channelTypes={channelTypes}
          handleDelete={handleDelete}
          handleCancel={handleCancel}
          meta={{ deletePrompt: 'Please make sure this channel is not associated with any active Bloomreach scenarios.' }}
        />

        <Toast
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          data-testid="bloomreach-account-toast"
          maxWidth="30vw"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          title={toast.title}
          description={toast.description}
          type={toast.type}
          open={toast.open}
          autoHideDuration={5000}
        />
      </div>
    </ThemeProvider>
  );
};

export default AccountSettings;
